import { Button, Chip, EditIcon, DesktopIcon } from "@bp/ui-components";
import { type CSSProperties, type ReactNode, useMemo } from "react";
import { useQuery } from "urql";
import { getFragmentData, graphql } from "../../client/gql";
import { fmtDate } from "../../utils/format";
import { moduleLabel } from "../../utils/modules";
import {
  appDisplayName,
  canonicalClientId,
  productIdFromClientId,
  productLabel,
} from "../../utils/product";
import { ProductMark } from "../brand";

// Colocated fragment: only the fields needed to resolve a client id → app name.
const LicensedProductList_Application = graphql(`
  fragment LicensedProductList_Application on Application {
    clientId
    name
  }
`);

const LicensedProductListQuery = graphql(`
  query LicensedProductList_Query {
    applications(options: { sort: { name: ASC } }) {
      ...LicensedProductList_Application
    }
  }
`);

export type LicensedAppEdge = {
  properties?: {
    notBefore?: string | null;
    notAfter?: string | null;
    licensedModules?: readonly (string | null)[] | null;
    tecartContractId?: string | null;
  } | null;
  node?: { uuid?: string | null; clientId?: string | null } | null;
  /** Set on a merged row: every application uuid the row stands for (canonical first). */
  mergedNodeUuids?: string[];
};

/**
 * Fold duplicate registrations of one product (bp-app-client + bp-frontend-client)
 * into a single row: the canonical client's node, the union of modules, the
 * widest term, and all node uuids for access checks.
 */
export function mergeDuplicateProducts(edges: readonly LicensedAppEdge[]): LicensedAppEdge[] {
  const groups = new Map<string, LicensedAppEdge[]>();
  for (const e of edges) {
    const key = canonicalClientId(e.node?.clientId ?? "");
    groups.set(key, [...(groups.get(key) ?? []), e]);
  }
  return [...groups.entries()].map(([canonical, group]) => {
    if (group.length === 1) {
      return group[0] as LicensedAppEdge;
    }
    const primary =
      group.find((e) => e.node?.clientId === canonical) ?? (group[0] as LicensedAppEdge);
    const modules = [...new Set(group.flatMap((e) => e.properties?.licensedModules ?? []))];
    const froms = group.map((e) => e.properties?.notBefore).filter(Boolean) as string[];
    const tos = group.map((e) => e.properties?.notAfter).filter(Boolean) as string[];
    return {
      ...primary,
      properties: {
        ...primary.properties,
        licensedModules: modules,
        notBefore: froms.length === group.length ? froms.sort()[0] : null,
        notAfter: tos.length === group.length ? tos.sort().at(-1) : null,
      },
      mergedNodeUuids: [
        primary.node?.uuid,
        ...group.filter((e) => e !== primary).map((e) => e.node?.uuid),
      ].filter(Boolean) as string[],
    };
  });
}

/** "Gebucht 01.01.2026 – 31.12.2026" / "ab …" / "bis …" / "unbefristet" — never dashes. */
export function licenseTerm(notBefore?: string | null, notAfter?: string | null): string {
  const from = notBefore ? fmtDate(notBefore) : null;
  const to = notAfter ? fmtDate(notAfter) : null;
  if (from && to) return `Gebucht ${from} – ${to}`;
  if (from) return `Gebucht ab ${from}`;
  if (to) return `Gebucht bis ${to}`;
  return "Gebucht · unbefristet";
}

/** Renders an org's licensed applications (product mark + resolved name + module chips + term). */
export function LicensedProductList({
  edges,
  emptyText = "Keine gebuchten Produkte.",
  editableClientIds,
  onEditModules,
  renderStatus,
}: {
  edges: readonly LicensedAppEdge[];
  emptyText?: string;
  /** Client ids whose module set can be edited (renders a pencil on that row). */
  editableClientIds?: readonly string[];
  onEditModules?: (clientId: string) => void;
  /** Optional per-row slot, e.g. whether a given profile can access the product. */
  renderStatus?: (edge: LicensedAppEdge) => ReactNode;
}) {
  const [{ data }] = useQuery({ query: LicensedProductListQuery });
  const apps = getFragmentData(LicensedProductList_Application, data?.applications ?? []);
  const nameByClientId = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of apps) {
      map.set(a.clientId, a.name);
    }
    return map;
  }, [apps]);

  // A merged row is the product, not one of its registrations.
  const resolveName = (e: LicensedAppEdge) => {
    const cid = e.node?.clientId ?? "";
    const pid = productIdFromClientId(cid);
    if (e.mergedNodeUuids && pid) {
      return productLabel(pid);
    }
    return appDisplayName(nameByClientId.get(cid), cid);
  };
  const sortedEdges = useMemo(
    () =>
      mergeDuplicateProducts(edges).sort((a, b) =>
        resolveName(a).localeCompare(resolveName(b), "de"),
      ),
    // resolveName depends on edges + nameByClientId
    [edges, nameByClientId],
  );

  if (edges.length === 0) {
    return <p style={{ color: "var(--color-grey)", margin: 0 }}>{emptyText}</p>;
  }

  const markBox: CSSProperties = {
    width: 40,
    height: 40,
    borderRadius: "var(--border-radius-m)",
    background: "var(--color-grey-lightest)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  };

  return (
    <div>
      {sortedEdges.map((e, i) => {
        const clientId = e.node?.clientId ?? "";
        const pid = productIdFromClientId(clientId);
        const name = resolveName(e);
        const modules = (e.properties?.licensedModules ?? []).filter(Boolean) as string[];
        const contract = e.properties?.tecartContractId;
        const term = licenseTerm(e.properties?.notBefore, e.properties?.notAfter);
        // A name that had to be derived from the client id keeps the id visible;
        // a merged product row is named on purpose and needs none.
        const showClientId = !e.mergedNodeUuids && name !== nameByClientId.get(clientId);
        return (
          <div
            key={e.node?.uuid ?? clientId ?? i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 13,
              padding: "14px 0",
              borderBottom:
                i < sortedEdges.length - 1 ? "1px solid var(--color-grey-lightest)" : undefined,
            }}
          >
            <span style={markBox}>
              {pid ? (
                <ProductMark id={pid} size={22} />
              ) : (
                <DesktopIcon style={{ width: 20, height: 20, color: "var(--color-grey)" }} />
              )}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "var(--font-size-m)" }}>{name}</div>
              <div
                style={{ fontSize: "var(--font-size-s)", color: "var(--color-grey)", marginTop: 2 }}
              >
                {term}
                {contract ? ` · Vertrag ${contract}` : ""}
                {showClientId && clientId ? ` · ${clientId}` : ""}
              </div>
            </div>
            {renderStatus?.(e)}
            {modules.length > 0 && (
              <div
                style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}
              >
                {modules.map((m) => (
                  <Chip key={m} value={moduleLabel(m)} isSmall />
                ))}
              </div>
            )}
            {onEditModules && editableClientIds?.includes(clientId) && (
              <Button
                hierarchy="tertiary"
                height="s"
                icon={<EditIcon />}
                ariaLabel="Module bearbeiten"
                onClick={() => onEditModules(clientId)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
